import {Address, WalletAssetMergeType} from "types/types";
import {AlphaAvatar, Button, ModalDrawer} from "../../ui";
import {useMatchChain, useWallet} from "hooks";
import {useEffect, useMemo, useState} from "react";
import {NumberFormatter} from "../../utils";
import {TransferIcon} from "../../assets/icon";
import {defineChain, encodeFunctionData, erc20Abi, http, parseUnits} from "viem";
import {NATIVE_TOKEN_ADDRESS} from "../../config";
import {FormattedMessage, useIntl} from "react-intl";

function Input({
                   onChange,
                   placeholder,
                   value,
                   maxLength,
                   type,
                   error,
                   size = "df"
               }: {
    onChange: (e: any) => void
    placeholder: string
    value: string
    maxLength: number
    type: string
    error: string
    size?: "sm" | "df"
}) {
    return <div className={`matchid-token-input-box ${"matchid-token-input-" + size}`}>
        <input placeholder={placeholder} onChange={onChange} maxLength={maxLength} type={type} value={value}
               className={`matchid-token-input ${error ? 'matchid-token-input-error' : ''}`}/>
        {error && <div className={"matchid-token-input-error-text"}>{error}</div>}
    </div>
}

export default function TokenSend({
                                      onClose,
                                      zIndex,
                                      token,
                                      onBack
                                  }: {
    onBack: () => void
    onClose: () => void
    zIndex: number
    token: WalletAssetMergeType
}) {
    const {list: chainList} = useMatchChain()
    const intl = useIntl()
    const {createWalletClient} = useWallet()
    const isNative = token.address.toLowerCase() == NATIVE_TOKEN_ADDRESS
    const chain = useMemo(() => {
        return chainList?.find((m) => m.id.toString() === token.chain_id)
    }, [chainList, token.chain_id])
    const walletClient = useMemo(() => {
        return createWalletClient({
            // @ts-ignore
            chain: defineChain(chain),
            transport: http()
        })
    }, [chain])

    const [amount, setAmount] = useState('')
    const [address, setAddress] = useState('')
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)

    const [txError, setTxError] = useState('')
    const transaction = useMemo(() => {
        const reg = /^0x[a-fA-F0-9]{40}$/
        if(!amount || !address || !reg.test(address)){
            return
        }
        //@ts-ignore
        const viemChain = defineChain(chain)
        const to = isNative ? address as `0x${string}` : token.address
        const value = isNative ? parseUnits(amount, parseInt(token?.decimals || "18")) : BigInt(0)
        const data = isNative ? "0x" : encodeFunctionData({
            abi:erc20Abi,
            functionName: 'transfer',
            args: [address as Address, parseUnits(amount, parseInt(token?.decimals || "18"))]
        })
        return {
            to:to as `0x${string}`,
            value,
            data,
            chain: viemChain
        }
    }, [amount, address])
    const onCal = async () => {
        try {
            setLoading(true)
            // @ts-ignore
            await walletClient?.prepareTransactionRequest(transaction)
        } catch (error: any) {
            console.error(error)
            setTxError(error.details || error.message)
        } finally {
            setLoading(false)
        }
    }

    const error = useMemo(() => {
        setTxError('')
        let amountError = ''
        let addressError = ''

        if (amount) {
            if (parseFloat(amount) <= 0) {
                amountError = intl.formatMessage({
                    id:"tokenAmountMin"
                })
            } else if ('balance' in token && parseFloat(amount) > (token.balance || 0)) {
                amountError = intl.formatMessage({
                    id:"tokenAmountMax"
                })
            }
        }

        if (address) {
            if (address.length < 42) {
                addressError = intl.formatMessage({
                    id:"tokenAddressError"
                })
            } else {
                const reg = /^0x[a-fA-F0-9]{40}$/
                if (!reg.test(address)) {
                    addressError = intl.formatMessage({
                        id:"tokenAddressError"
                    })
                }
            }
        }
        if (amount && address && !amountError && !addressError && walletClient) {
            onCal()
        }

        return {
            amount: amountError,
            address: addressError,
        }
    }, [amount, token, chain, address,walletClient])

    const onChangeAmount = (e: any) => {
        const value = e.target.value
        if (value.match(/^\d*\.?\d*$/)) {
            setAmount(value)
        }
    }

    const canSend = useMemo(() => {
        return !error.amount && !error.address && amount && address
    }, [error])

    const onNext = async () => {
        setSending(true)
        if(transaction){
            await walletClient?.sendTransaction(transaction)
        }
        onClose()

    }

    useEffect(() => {
        const receiveMessage = (event: any) => {
            if(event.data){
                if(event.data.source=="match-wallet"){
                    if(event.data.method=="sendTransaction" && event.data.messageId=="openIframe"){
                        onBack()
                        setSending(false)
                    }
                }
            }
        }
        window.addEventListener('message', receiveMessage)
        return () => {
            window.removeEventListener('message', receiveMessage)
        }
    }, []);
    return <ModalDrawer isOpen onClose={onClose} zIndex={zIndex} title={<FormattedMessage id={"send"}/>} onBack={onBack}>
        <div className={`matchid-token-send-box`}>
            <div className={"matchid-token-send-content"}>
                <div className={`matchid-token-amount-content`}>
                    <div className={`matchid-token-amount-header`}>
                        <div className={`matchid-token-amount-title`}><FormattedMessage id={"amount"}/></div>
                        <div className={`matchid-token-amount-chain`}>
                            {token.icon ? <img src={token?.icon} alt={token?.symbol}
                                               className={`matchid-token-amount-chain-icon`}/> :
                                <AlphaAvatar name={token.symbol||token.name||''} size={16}/>}
                            <span>{token?.symbol}</span>
                        </div>
                    </div>
                    <Input
                        type={"text"}
                        onChange={onChangeAmount}
                        placeholder={intl.formatMessage({
                            id:"amountPlaceholder"
                        })}
                        value={amount}
                        maxLength={40}
                        error={error.amount || txError}
                    />
                    <div className={`matchid-token-amount-footer`}>
                        <div className={`matchid-token-amount-title`}><FormattedMessage id={"balance"}/>:</div>
                        <div className={`matchid-token-amount-value`}>
                            <NumberFormatter value={token.balance} tFixNum={10} suffix={" " + token.symbol}/>
                        </div>
                    </div>
                    <TransferIcon className={"matchid-token-amount-transfer"}/>
                </div>

                <div className={`matchid-token-address-content`}>
                    <div className={`matchid-token-address-header`}>
                        <div className={`matchid-token-address-title`}><FormattedMessage id={"receiveTitle"}/></div>
                    </div>
                    <Input
                        type={"text"}
                        size={"sm"}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder={intl.formatMessage({
                            id:"receivePlaceholder"
                        })}
                        value={address}
                        maxLength={42}
                        error={error.address}
                    />
                </div>
            </div>
            <Button size={"lg"} block highlight disabled={!canSend || !!txError} onClick={onNext}
                    loading={loading||sending}><FormattedMessage id={"next"}/></Button>
        </div>
    </ModalDrawer>
}